import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { analyticsAPI } from "components/Apis/analytics";

const getYesterdayDateString = () => {
    const today = new Date();
    let yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const year = yesterday.getFullYear();
    const month = String(yesterday.getMonth() + 1).padStart(2, '0');
    const day = String(yesterday.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const DEFAULT_DELIVERY_FILTER_ID = 'default_delivery_date';
const DEFAULT_STATUS_FILTER_ID = 'default_sylectus_status_completed';
const COMPLETED_STATUS_VALUE = "Completed";

export const fetchFilterOptions = createAsyncThunk(
    'orderAnalytics/fetchFilterOptions',
    async (_, { rejectWithValue }) => {
        try {
            const response = await analyticsAPI.getFilters();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchGroupOptions = createAsyncThunk(
    'orderAnalytics/fetchGroupOptions',
    async (_, { rejectWithValue }) => {
        try {
            const response = await analyticsAPI.getGroupOptions();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchAnnotationOptions = createAsyncThunk(
    'orderAnalytics/fetchAnnotationOptions',
    async (_, { rejectWithValue, getState }) => {
        try {
            const grouping = (getState().analytics.groupOptions?.data || [])
                .filter((option) => option.selected)
                .map((option) => option.id);
            const response = await analyticsAPI.getAnnotations(grouping);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchOrderingOptions = createAsyncThunk(
    'orderAnalytics/fetchOrderingOptions',
    async (_, { rejectWithValue }) => {
        try {
            const response = await analyticsAPI.getOrderingOptions();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchAnalyticsData = createAsyncThunk(
    'orderAnalytics/fetchAnalyticsData',
    async (payload = {}, { getState, rejectWithValue }) => {
        try {
            const analyticsState = getState().analytics;
            const filters = Object.values(analyticsState.engagedFilters || {});
            const annotations = analyticsState.selectedAnnotations || [];
            const ordering = analyticsState.ordering || null;
            const grouping = (analyticsState.groupOptions?.data || [])
                .filter((opt) => opt.selected)
                .map((opt) => opt.id);
            const pagination = analyticsState.pagination || { page: 1, pageSize: 25 };
            const page = payload.page ?? pagination.page ?? 1;
            const pageSize = payload.pageSize ?? pagination.pageSize ?? 25;

            const response = await analyticsAPI.getData(
                page,
                pageSize,
                filters,
                grouping,
                annotations,
                ordering
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

const initialState = {
    engagedFilters: {
        [DEFAULT_DELIVERY_FILTER_ID]: {
            filteredEntity: { id: 'delivery_date', label: 'Delivery Date', type: 'date' },
            filterValue: { to: getYesterdayDateString() },
            filterOperator: 'range',
        },
        [DEFAULT_STATUS_FILTER_ID]: {
            filteredEntity: { id: 'sylectus_status', label: 'Status', type: 'select' },
            filterValue: { value: COMPLETED_STATUS_VALUE },
            filterOperator: 'exact',
        },
    },
    ordering: '-delivery_date',
    selectedAnnotations: ['order', 'service_team', 'delivery_date', 'pickup_location', 'delivery_location', 'customer', 'revenue', 'margin', 'margin_percent'],
    pagination: {
        page: 1,
        pageSize: 25,
        totalRecords: 0,
        totalPages: 1,
    },
    filterOptions: {
        loading: false,
        error: null,
        data: null,
    },
    groupOptions: {
        loading: false,
        error: null,
        data: null,
    },
    annotationOptions: {
        loading: false,
        error: null,
        data: null,
    },
    orderingOptions: {
        loading: false,
        error: null,
        data: null,
    },
    analyticsData: {
        loading: false,
        error: null,
        data: null,
    },
};

const ordersSlice = createSlice({
    name: 'orderAnalytics',
    initialState,
    reducers: {
        // Define your synchronous reducers 
        // filters
        clearFilters: (state) => {
            state.engagedFilters = {};
        },
        addNewFilter: (state, action) => {
            // create an id for the new filter
            const newFilterId = `filter_${Date.now()}`;
            state.engagedFilters[newFilterId] = {
                filteredEntity: null,
                filterValue: null,
                filterOperator: 'range',
            };
        },
        removeFilter: (state, action) => {
            const filterId = action.payload;
            delete state.engagedFilters[filterId];
        },
        setFilteredEntity: (state, action) => {
            const { filterId, filteredEntity } = action.payload;
            const current = state.engagedFilters[filterId] || {};
            const previousType = current.filteredEntity?.type;
            const nextType = filteredEntity?.type;
            const hasEntityTypeChanged = previousType !== nextType;

            state.engagedFilters[filterId] = {
                ...current,
                filteredEntity: filteredEntity || null,
                // Reset values if entity changes so we don't carry stale params
                filterValue: hasEntityTypeChanged ? {} : current.filterValue,
                filterOperator: hasEntityTypeChanged
                    ? (nextType === 'text'
                        ? 'icontains'
                        : nextType === 'select'
                            ? 'exact'
                            : 'range')
                    : (current.filterOperator || (nextType === 'text' ? 'icontains' : nextType === 'select' ? 'exact' : 'range')),
            };
        },
        setFilterValue: (state, action) => {
            const { filterId, filterValue } = action.payload;
            const current = state.engagedFilters[filterId] || {};
            const previousValue = current.filterValue;
            const shouldMergeObjects =
                previousValue &&
                typeof previousValue === 'object' &&
                !Array.isArray(previousValue) &&
                filterValue &&
                typeof filterValue === 'object' &&
                !Array.isArray(filterValue);

            const hydratedFilterValue = shouldMergeObjects
                ? { ...previousValue, ...filterValue }
                : filterValue;

            state.engagedFilters[filterId] = {
                ...current,
                filterValue: hydratedFilterValue,
                justAdded: false,
            };
        },
        setFilterOperator: (state, action) => {
            const { filterId, filterOperator } = action.payload;
            const current = state.engagedFilters[filterId] || {};
            state.engagedFilters[filterId] = {
                ...current,
                filterOperator,
                filterValue: {},
            };
        },
        setSelectedAnnotations: (state, action) => {
            state.selectedAnnotations = action.payload || [];
        },
        setPage: (state, action) => {
            state.pagination.page = action.payload;
        },
        setPageSize: (state, action) => {
            state.pagination.pageSize = action.payload;
            state.pagination.page = 1;
        },

        // ordering
        setOrdering: (state, action) => {
            if (action.payload === state.ordering) {
                state.ordering = `-${action.payload}`;
                return;
            }
            else {
                state.ordering = action.payload;
            }
        },
        // grouping
        setGrouping: (state, action) => {
            const selectedGroups = Array.isArray(action.payload)
                ? action.payload
                : action.payload
                    ? [action.payload]
                    : [];
            const selectedGroupSet = new Set(selectedGroups);
            if (Array.isArray(state.groupOptions.data)) {
                const indexById = new Map(state.groupOptions.data.map((opt, idx) => [opt.id, idx]));
                state.groupOptions.data = state.groupOptions.data
                    .map((option) => ({
                        ...option,
                        selected: selectedGroupSet.has(option.id),
                    }))
                    .sort((a, b) => {
                        const aOrder = selectedGroups.indexOf(a.id);
                        const bOrder = selectedGroups.indexOf(b.id);
                        const aSelected = aOrder !== -1;
                        const bSelected = bOrder !== -1;
                        if (aSelected && bSelected) return aOrder - bOrder;
                        if (aSelected) return -1;
                        if (bSelected) return 1;
                        return (indexById.get(a.id) ?? 0) - (indexById.get(b.id) ?? 0);
                    });
            }
            // activate all annotations for the new grouping
            if (selectedGroups.length > 0) {
                if (Array.isArray(state.annotationOptions.data)) {
                    state.annotationOptions.data.forEach(option => {
                        if (!state.selectedAnnotations.includes(option.id)) {
                            state.selectedAnnotations.push(option.id);
                        }
                    });
                }
                // set ordering to null when grouping changes
                if (state.ordering) {
                    state.ordering = selectedGroups[0];
                }
            }
            else {
                state.selectedAnnotations = initialState.selectedAnnotations;
                state.ordering = initialState.ordering;
            }
        },

        // set annotations
        setAnnotations: (state, action) => {
            const selectedAnnotations = action.payload;
            state.annotationOptions.data.forEach(option => {
                option.selected = selectedAnnotations.includes(option.id);
            });
        },
        applyTrendsConfiguration: (state, action) => {
            const config = action.payload || {};

            if (config.engagedFilters && typeof config.engagedFilters === 'object' && !Array.isArray(config.engagedFilters)) {
                state.engagedFilters = config.engagedFilters;
            }

            if (Array.isArray(config.selectedAnnotations)) {
                state.selectedAnnotations = config.selectedAnnotations;
            }

            if (Array.isArray(config.selectedGroups) && Array.isArray(state.groupOptions.data)) {
                const selectedGroupSet = new Set(config.selectedGroups);
                const indexById = new Map(state.groupOptions.data.map((opt, idx) => [opt.id, idx]));
                state.groupOptions.data = state.groupOptions.data
                    .map((option) => ({
                        ...option,
                        selected: selectedGroupSet.has(option.id),
                    }))
                    .sort((a, b) => {
                        const aOrder = config.selectedGroups.indexOf(a.id);
                        const bOrder = config.selectedGroups.indexOf(b.id);
                        const aSelected = aOrder !== -1;
                        const bSelected = bOrder !== -1;
                        if (aSelected && bSelected) return aOrder - bOrder;
                        if (aSelected) return -1;
                        if (bSelected) return 1;
                        return (indexById.get(a.id) ?? 0) - (indexById.get(b.id) ?? 0);
                    });
            }

            if (typeof config.ordering === 'string' || config.ordering === null) {
                state.ordering = config.ordering;
            }

            if (config.pageSize !== undefined && config.pageSize !== null) {
                const nextPageSize = Number(config.pageSize);
                if (!Number.isNaN(nextPageSize) && nextPageSize > 0) {
                    state.pagination.pageSize = nextPageSize;
                }
            }

            state.pagination.page = 1;
        },
    },
    extraReducers: (builder) => {
        // Handle asynchronous actions here
        builder
            .addCase(fetchFilterOptions.pending, (state) => {
                state.filterOptions.loading = true;
                state.filterOptions.error = null;
            })
            .addCase(fetchFilterOptions.fulfilled, (state, action) => {
                state.filterOptions.loading = false;
                state.filterOptions.data = action.payload;
                const deliveryDateFilter = action.payload?.find((option) => option.id === 'delivery_date');
                if (deliveryDateFilter && state.engagedFilters[DEFAULT_DELIVERY_FILTER_ID]) {
                    const existing = state.engagedFilters[DEFAULT_DELIVERY_FILTER_ID];
                    const hasCustomValue = existing.filterValue && Object.keys(existing.filterValue).length > 0;
                    state.engagedFilters[DEFAULT_DELIVERY_FILTER_ID] = {
                        ...existing,
                        filteredEntity: deliveryDateFilter,
                        filterOperator: existing.filterOperator || 'range',
                        filterValue: hasCustomValue ? existing.filterValue : { to: getYesterdayDateString() },
                    };
                }
                const statusFilter = action.payload?.find((option) => option.id === 'sylectus_status');
                if (statusFilter && state.engagedFilters[DEFAULT_STATUS_FILTER_ID]) {
                    const existing = state.engagedFilters[DEFAULT_STATUS_FILTER_ID];
                    const hasCustomValue = existing.filterValue && Object.keys(existing.filterValue).length > 0;
                    state.engagedFilters[DEFAULT_STATUS_FILTER_ID] = {
                        ...existing,
                        filteredEntity: statusFilter,
                        filterOperator: existing.filterOperator || 'exact',
                        filterValue: hasCustomValue ? existing.filterValue : { value: COMPLETED_STATUS_VALUE },
                    };
                }
            })
            .addCase(fetchFilterOptions.rejected, (state, action) => {
                state.filterOptions.loading = false;
                state.filterOptions.error = action.payload;
            })
            .addCase(fetchGroupOptions.pending, (state) => {
                state.groupOptions.loading = true;
                state.groupOptions.error = null;
            })
            .addCase(fetchGroupOptions.fulfilled, (state, action) => {
                state.groupOptions.loading = false;
                const incoming = action.payload || [];
                const alreadySelected = new Set(incoming.filter((opt) => opt.selected).map((opt) => opt.id));
                state.groupOptions.data = incoming.map((opt) => ({
                    ...opt,
                    selected: alreadySelected.size > 0 ? alreadySelected.has(opt.id) : false,
                }));
            })
            .addCase(fetchGroupOptions.rejected, (state, action) => {
                state.groupOptions.loading = false;
                state.groupOptions.error = action.payload;
            })
            .addCase(fetchOrderingOptions.pending, (state) => {
                state.orderingOptions.loading = true;
                state.orderingOptions.error = null;
            })
            .addCase(fetchOrderingOptions.fulfilled, (state, action) => {
                state.orderingOptions.loading = false;
                state.orderingOptions.data = action.payload;
            })
            .addCase(fetchOrderingOptions.rejected, (state, action) => {
                state.orderingOptions.loading = false;
                state.orderingOptions.error = action.payload;
            })
            .addCase(fetchAnnotationOptions.pending, (state) => {
                state.annotationOptions.loading = true;
                state.annotationOptions.error = null;
            })
            .addCase(fetchAnnotationOptions.fulfilled, (state, action) => {
                state.annotationOptions.loading = false;
                state.annotationOptions.data = action.payload;
            })
            .addCase(fetchAnnotationOptions.rejected, (state, action) => {
                state.annotationOptions.loading = false;
                state.annotationOptions.error = action.payload;
            })
            .addCase(fetchAnalyticsData.pending, (state) => {
                state.analyticsData.loading = true;
                state.analyticsData.error = null;
            })
            .addCase(fetchAnalyticsData.fulfilled, (state, action) => {
                state.analyticsData.loading = false;
                state.analyticsData.data = action.payload;
                const count = action.payload?.count ?? (action.payload?.results?.length || 0);
                const currentPage = action.meta?.arg?.page ?? state.pagination.page;
                const pageSize = action.meta?.arg?.pageSize ?? state.pagination.pageSize;
                state.pagination.totalRecords = count;
                state.pagination.page = currentPage;
                state.pagination.pageSize = pageSize;
                state.pagination.totalPages = Math.max(1, Math.ceil(count / pageSize));
            })
            .addCase(fetchAnalyticsData.rejected, (state, action) => {
                state.analyticsData.loading = false;
                state.analyticsData.error = action.payload;
            });
    }
});

export const {
    clearFilters,
    addNewFilter,
    removeFilter,
    setFilteredEntity,
    setFilterValue,
    setFilterOperator,
    setSelectedAnnotations,
    setPage,
    setPageSize,
    setOrdering,
    setGrouping,
    applyTrendsConfiguration,
} = ordersSlice.actions;
export default ordersSlice.reducer;
